import { useState, useEffect, useRef } from "react";
import { X, Send, Clock, MessageCircle, Paperclip, Pencil, Trash2, Check, Image, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupportChat = ({ isOpen, onClose, token }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const didInitScrollRef = useRef(false);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    if (isOpen && token) {
      didInitScrollRef.current = false;
      fetchMessages();
      markAsRead();
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, token]);

  // Auto-scroll only when the user is already near the bottom (don't yank them
  // down while they're scrolling up to read history). Always scroll on first load.
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    if (!didInitScrollRef.current && messages.length) {
      scrollToBottom();
      didInitScrollRef.current = true;
      return;
    }
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) scrollToBottom();
  }, [messages]);

  // Close context menu on click outside
  useEffect(() => {
    const handler = () => setContextMenu(null);
    if (contextMenu) window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [contextMenu]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/chat/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(res.data);
    } catch (e) { console.error(e); }
  };

  const markAsRead = async () => {
    try { await axios.put(`${API}/chat/mark-read`, {}, { headers: { Authorization: `Bearer ${token}` } }); } catch (e) {}
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/messages`, { message: newMessage }, { headers: { Authorization: `Bearer ${token}` } });
      setNewMessage("");
      fetchMessages();
    } catch (e) { toast.error("Ошибка отправки"); }
    finally { setSending(false); }
  };

  const handleEdit = async (id) => {
    if (!editText.trim()) return;
    try {
      await axios.put(`${API}/chat/messages/${id}`, { message: editText }, { headers: { Authorization: `Bearer ${token}` } });
      setEditingId(null);
      setEditText("");
      fetchMessages();
    } catch (e) { toast.error("Ошибка редактирования"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/chat/messages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchMessages();
      toast.success("Сообщение удалено");
    } catch (e) { toast.error("Ошибка удаления"); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ALLOWED = ["jpg","jpeg","png","gif","webp","bmp","pdf","doc","docx","xls","xlsx","txt","csv","zip","rar"];
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED.includes(ext)) {
      toast.error(`Тип «.${ext}» не поддерживается. Разрешены: фото, PDF, документы.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`Файл слишком большой (${(file.size/1048576).toFixed(1)} МБ). Максимум 10 МБ.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setSending(true);
    try {
      await axios.post(`${API}/chat/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      toast.success("Файл отправлен");
      fetchMessages();
    } catch (e) { toast.error(e.response?.data?.detail || "Ошибка загрузки"); }
    finally {
      setSending(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleContextMenu = (e, msg) => {
    if (msg.sender !== "user") return;
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, msg });
  };

  const formatTime = (d) => new Date(d).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  const formatSize = (b) => b < 1024 ? `${b} Б` : b < 1048576 ? `${(b/1024).toFixed(1)} КБ` : `${(b/1048576).toFixed(1)} МБ`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-4" data-testid="support-chat">
      <div className="w-full max-w-md h-[600px] max-h-[calc(100dvh-1.5rem)] bg-[#000000] rounded-lg border border-[#1F1F1F] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-[#0A0A0C] border-b border-[#1F1F1F] p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle size={20} className="text-[#EDEDED]" />
              </div>
              <div>
                <h3 className="font-bold text-white">24/7 Поддержка</h3>
                <div className="flex items-center gap-1 text-xs text-[#A1A1AA]">
                  <Clock size={12} /><span>Онлайн</span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-[#A1A1AA] hover:text-white transition-colors" data-testid="close-chat-btn">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="flex justify-start">
              <div className="max-w-[80%]">
                <div className="bg-[#1A1A1A] rounded-lg rounded-tl-none px-4 py-3 text-white text-sm">
                  Здравствуйте! Чем могу помочь?
                </div>
              </div>
            </div>
          )}

          {messages.map((msg) => {
            const isUser = msg.sender === "user";
            const isEditing = editingId === msg.id;

            return (
              <div key={msg.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[80%]" onContextMenu={(e) => handleContextMenu(e, msg)}>
                  {/* Editing mode */}
                  {isEditing ? (
                    <div className="flex items-end gap-2">
                      <input
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleEdit(msg.id)}
                        className="flex-1 bg-[#1A1A1A] border border-white/50 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                        autoFocus
                        data-testid="edit-message-input"
                      />
                      <button onClick={() => handleEdit(msg.id)} className="p-1.5 text-[#EDEDED] hover:text-white" data-testid="confirm-edit-btn">
                        <Check size={16} />
                      </button>
                      <button onClick={() => setEditingId(null)} className="p-1.5 text-[#A1A1AA] hover:text-white">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Attachments */}
                      {msg.attachments?.map((att, i) => (
                        <div key={i} className="mb-1">
                          {att.is_image ? (
                            <a href={`${API}/chat/files/${att.filename}`} target="_blank" rel="noreferrer">
                              <img
                                src={`${API}/chat/files/${att.filename}`}
                                alt={att.original_name}
                                className="max-w-full rounded-lg border border-[#1F1F1F] max-h-48 object-cover"
                              />
                            </a>
                          ) : (
                            <a
                              href={`${API}/chat/files/${att.filename}`}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-2 bg-[#1A1A1A] rounded-lg px-3 py-2 border border-[#1F1F1F] hover:border-[#2E2E2E]/40 transition-colors"
                            >
                              <FileText size={18} className="text-[#EDEDED]" />
                              <div className="min-w-0">
                                <p className="text-white text-sm truncate">{att.original_name}</p>
                                <p className="text-[#71717A] text-xs">{formatSize(att.size)}</p>
                              </div>
                            </a>
                          )}
                        </div>
                      ))}

                      {/* Text bubble */}
                      {msg.message && (
                        <div className={`rounded-lg px-4 py-2.5 text-sm ${
                          isUser
                            ? "bg-[#FF8A1E] text-white rounded-tr-none"
                            : "bg-[#1A1A1A] text-white rounded-tl-none"
                        }`}>
                          {msg.message}
                        </div>
                      )}

                      {/* Meta info */}
                      <div className={`flex items-center gap-2 mt-1 text-xs text-[#71717A] ${isUser ? "justify-end mr-1" : "ml-1"}`}>
                        {msg.edited && <span className="italic">ред.</span>}
                        <span>{formatTime(msg.created_at)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSend} className="p-3 border-t border-[#1F1F1F] bg-[#0A0A0C]">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2.5 text-[#A1A1AA] hover:text-[#EDEDED] transition-colors"
              data-testid="attach-file-btn"
            >
              <Paperclip size={20} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileUpload}
              accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar"
            />
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Сообщение..."
              className="flex-1 bg-[#1A1A1A] border border-[#1F1F1F] rounded-full px-4 py-2.5 text-white text-sm placeholder-[#71717A] focus:border-white focus:outline-none"
              disabled={sending}
              data-testid="chat-message-input"
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="w-10 h-10 rounded-full bg-white hover:bg-[#E5E5E5] disabled:opacity-40 flex items-center justify-center transition-colors"
              data-testid="send-message-btn"
            >
              <Send size={16} className="text-black" />
            </button>
          </div>
        </form>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-[80] bg-[#1A1A1A] border border-[#1F1F1F] rounded-lg shadow-xl py-1 min-w-[140px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          data-testid="context-menu"
        >
          {contextMenu.msg.message && (
            <button
              onClick={() => { setEditingId(contextMenu.msg.id); setEditText(contextMenu.msg.message); setContextMenu(null); }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-white hover:bg-[#1A1A1A]/10 transition-colors"
              data-testid="edit-message-btn"
            >
              <Pencil size={14} /> Редактировать
            </button>
          )}
          <button
            onClick={() => { handleDelete(contextMenu.msg.id); setContextMenu(null); }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#F87171] hover:bg-[#F87171]/10 transition-colors"
            data-testid="delete-message-btn"
          >
            <Trash2 size={14} /> Удалить
          </button>
        </div>
      )}
    </div>
  );
};

export default SupportChat;
